import Head from 'next/head';
import config from 'config';
import { useTranslation } from 'hooks/useTranslation';
import { getFullImageUrl } from 'utils/image';
import type { QunoAuthor } from 'types/Author';

type Props = {
  entry: QunoAuthor;
};

const authorPageLink = ({
  locale,
  slug,
  firstLevel,
}: Record<string, string>): string => {
  const { production } = config.app.domains.proxy;

  if (slug) {
    return `https://${production}/${locale}/${firstLevel}/${slug}`;
  }

  return `https://${production}`;
};

const schemaAuthorImg = (imageUrl: string): string[] => {
  const fullImageUrl = getFullImageUrl(imageUrl);

  return [
    `${fullImageUrl}?fit=fill&w=110&h=110`,
    `${fullImageUrl}?fit=fill&w=220&h=220`,
    `${fullImageUrl}?fit=fill&w=320&h=320`,
  ];
};

const StructuredData = ({ entry }: Props): JSX.Element => {
  const { locale, t } = useTranslation();

  const alumniOfSchema = {
    alumniOf: {
      '@type': entry.alumniOfType || null,
      name: entry.alumniOfName || null,
      url: entry.alumniOfUrl || null,
      sameAs: entry.alumniOfUrl || null,
    },
  };

  const affiliationSchema = {
    affiliation: {
      '@type': entry.affiliationType || null,
      name: entry.affiliationName || null,
      url: entry.affiliationUrl || null,
      sameAs: entry.affiliationUrl || null,
    },
  };

  const baseSchema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: entry.name,
    description: entry.metaDescription,
    jobTitle: entry.jobTitle,
    honorificPrefix: entry.honorificPrefix,
    honorificSuffix: entry.honorificSuffix,
    sameAs: entry.sameAs,
    knowsAbout: entry.knowsAbout,
    ...(entry.alumniOfType &&
      entry.alumniOfName &&
      entry.alumniOfUrl &&
      alumniOfSchema),
    ...(entry.affiliationType && entry.affiliationName && entry.affiliationUrl
      ? affiliationSchema
      : null),
    url: authorPageLink({
      locale,
      slug: entry.slug || '',
      firstLevel: t('authors').toLowerCase(),
    }),
    image: schemaAuthorImg(entry.photo.url),
  };

  return (
    <Head>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(baseSchema) }}
      />
    </Head>
  );
};

export default StructuredData;
